import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Spinner } from '@/components/Spinner';
import { toast } from 'sonner';
import { createStripeCheckout, createStripePortalSession } from '@/utils/stripeUtils';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CreditCard, ShieldCheck, X, Clock } from 'lucide-react';
import { trackConversion } from '@/utils/rewardfulUtils';

const Subscription = () => {
  const [isLoadingUI, setIsLoadingUI] = useState(false);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const navigate = useNavigate();
  const { user, hasActiveSubscription, checkSubscription, isLoading: authLoading } = useAuth();

  const hasActiveSub = hasActiveSubscription();

  const getSubscriptionDisplayInfo = () => {
    console.log('[Subscription] Current user subscription data:', user?.subscription);
    
    if (!user?.subscription) {
      return { status: 'inactive' as const, displayText: 'Inactiva', isTrialing: false };
    }
    
    const subscription = user.subscription;
    const now = new Date();
    
    // Check if explicitly marked as trialing
    if (subscription.isTrialing) {
      console.log('[Subscription] User is explicitly marked as trialing');
      return { status: 'trialing' as const, displayText: 'Período de Prueba', isTrialing: true };
    }
    
    // Check if trial end date is in the future
    if (subscription.trialEnd) {
      const trialEndDate = new Date(subscription.trialEnd);
      console.log('[Subscription] Trial end date:', trialEndDate, 'Current date:', now);
      if (trialEndDate > now) {
        console.log('[Subscription] Trial is still active based on end date');
        return { status: 'trialing' as const, displayText: 'Período de Prueba', isTrialing: true };
      }
    }
    
    // Check subscription status
    if (subscription.status === 'trialing') {
      console.log('[Subscription] Status is trialing');
      return { status: 'trialing' as const, displayText: 'Período de Prueba', isTrialing: true };
    }
    
    if (subscription.status === 'active') {
      console.log('[Subscription] Status is active (not trial)');
      return { status: 'active' as const, displayText: 'Activa', isTrialing: false };
    }
    
    console.log('[Subscription] Default to inactive');
    return { status: 'inactive' as const, displayText: 'Inactiva', isTrialing: false };
  };

  useEffect(() => {
    sessionStorage.setItem('redirect_to_subscription_skipped', 'true');
    
    if (user?.id) {
      try {
        localStorage.setItem('had_subscription_page_view', 'true');
      } catch (e) {
        console.error("Error setting localStorage flag:", e);
      }
    }
  }, [user]);

  const redirectToCheckout = async () => {
    if (!user || !user.email) {
      toast.error('Por favor, inicia sesión para continuar');
      navigate('/login');
      return;
    }
    
    setIsLoadingStripe(true);
    try {
      const checkoutUrl = await createStripeCheckout(user.email);
      if (checkoutUrl) {
        trackConversion(user.email, { status: 'subscription_checkout_initiated' });
        window.location.href = checkoutUrl;
      } else {
        throw new Error('No se pudo obtener la URL de pago');
      }
    } catch (error) {
      console.error("[Subscription] Error creating checkout session:", error);
      toast.error('Error al iniciar el proceso de pago. Por favor, inténtalo de nuevo.');
      setIsLoadingStripe(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !user.email) {
      toast.error('Por favor, inicia sesión para gestionar tu suscripción.');
      navigate('/login');
      return;
    }
    
    setIsLoadingStripe(true);
    
    try {
      console.log("[Subscription] Creating portal session for email:", user.email);
      
      const portalUrl = await createStripePortalSession(user.email);
      
      if (!portalUrl) {
        throw new Error("No se pudo obtener la URL del portal");
      }
      
      console.log("[Subscription] Portal URL received:", portalUrl);
      
      // Direct redirection
      window.location.href = portalUrl;
      
    } catch (error) {
      console.error("[Subscription] Error creating portal session:", error);
      toast.error(`Error al acceder al portal: ${error.message}`);
      setIsLoadingStripe(false);
    }
  };

  // Simplified loading state - only show if auth is actually loading
  if (authLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center py-12 px-4 bg-gradient-to-br from-white to-mint/5">
          <Card className="w-full max-w-md text-center p-6 border-none shadow-lg rounded-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-mint to-skyblue bg-clip-text text-transparent">
                Cargando...
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center py-4">
                <Spinner size="lg" />
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if the user has an active subscription or is in trial
  const subscriptionInfo = getSubscriptionDisplayInfo();
  const isSubscriptionActiveOrTrial = subscriptionInfo.status === 'active' || subscriptionInfo.status === 'trialing';

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow py-16 px-4 bg-gradient-to-br from-white via-mint/5 to-skyblue/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-mint to-skyblue bg-clip-text text-transparent mb-3">
              {isSubscriptionActiveOrTrial ? 'Tu Suscripción Premium' : 'Impulsa tu experiencia vegana'}
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {isSubscriptionActiveOrTrial 
                ? 'Disfruta de acceso completo a todas las funciones premium de Guía Vegana'
                : 'Desbloquea todas las funciones y empieza a transformar tu alimentación con Guía Vegana'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <Card className="h-full border-none shadow-lg rounded-2xl overflow-hidden bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-mint to-skyblue"></div>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Beneficios Premium</span>
                    {isSubscriptionActiveOrTrial && (
                      <span className={`text-xs font-normal px-3 py-1 rounded-full ${
                        subscriptionInfo.status === 'trialing' 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-mint/10 text-mint'
                      }`}>
                        {subscriptionInfo.status === 'trialing' ? 'En Prueba' : 'Activo'}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Todo lo que obtendrás con tu suscripción
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-6">
                  <ul className="space-y-3">
                    {[
                      'Acceso a todas las recetas exclusivas',
                      'Planificador de menú semanal personalizado',
                      'Cálculo automático de nutrientes',
                      'Listas de compra generadas automáticamente',
                      'Sin publicidad ni interrupciones',
                      'Soporte prioritario'
                    ].map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-r from-mint to-skyblue flex items-center justify-center text-white shrink-0 mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            <div className="md:col-span-7">
              <Card className="border-none shadow-lg rounded-2xl overflow-hidden bg-white/90 backdrop-blur-sm">
                <div className={`absolute top-0 left-0 right-0 h-2 ${
                  isSubscriptionActiveOrTrial 
                    ? (subscriptionInfo.status === 'trialing' ? 'bg-blue-500' : 'bg-mint') 
                    : 'bg-gradient-to-r from-orange to-yellow'
                }`}></div>
                <CardHeader>
                  <CardTitle>
                    {isSubscriptionActiveOrTrial ? 'Gestionar Suscripción' : 'Comienza tu prueba gratuita'}
                  </CardTitle>
                  <CardDescription>
                    {isSubscriptionActiveOrTrial
                      ? 'Administra tu plan actual o realiza cambios'
                      : 'Prueba todas las funciones premium sin compromiso'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {isSubscriptionActiveOrTrial ? (
                    <>
                      <div className={`rounded-xl p-5 border ${
                        subscriptionInfo.status === 'trialing' 
                          ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'
                          : 'bg-gradient-to-r from-mint/10 to-skyblue/10 border-mint/20'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-gray-800">Estado de suscripción</h3>
                          <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                            subscriptionInfo.status === 'trialing'
                              ? 'bg-blue-200 text-blue-700'
                              : 'bg-mint/20 text-mint'
                          }`}>
                            {subscriptionInfo.status === 'trialing' ? 'En Prueba' : 'Activo'}
                          </span>
                        </div>
                        
                        {subscriptionInfo.isTrialing && user?.subscription.trialEnd && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-2 mb-1">
                              <Clock className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800">Período de Prueba</span>
                            </div>
                            <div className="text-sm text-blue-700">
                              Tu prueba gratuita termina el {' '}
                              {new Date(user.subscription.trialEnd).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-blue-600 mt-1">
                              Después se cobrará 9.90€/mes automáticamente
                            </div>
                          </div>
                        )}
                        
                        {!subscriptionInfo.isTrialing && user?.subscription.endDate && (
                          <div className="mt-4">
                            <div className="text-sm text-gray-600 mb-1">Próxima renovación</div>
                            <div className="text-gray-800 font-medium">
                              {new Date(user.subscription.endDate).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <div className="text-sm text-gray-600 mb-1">Plan actual</div>
                          <div className="text-gray-800 font-medium">Premium Mensual</div>
                          <div className={`font-bold mt-1 ${
                            subscriptionInfo.status === 'trialing' ? 'text-blue-600' : 'text-mint'
                          }`}>
                            {subscriptionInfo.isTrialing ? 'Gratis durante la prueba' : '9.90€/mes'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Button
                          onClick={handleManageSubscription}
                          disabled={isLoadingStripe}
                          className={`w-full h-12 font-medium ${
                            subscriptionInfo.status === 'trialing'
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90'
                              : 'bg-gradient-to-r from-mint to-skyblue text-white hover:opacity-90'
                          }`}
                        >
                          {isLoadingStripe ? (
                            <span className="flex items-center gap-2">
                              <Spinner size="sm" /> Accediendo al portal...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4" /> Gestionar Suscripción
                            </span>
                          )}
                        </Button>
                        
                        <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-700">
                              <p className="font-medium mb-1">¿Quieres cancelar tu suscripción?</p>
                              <p>Puedes hacerlo fácilmente desde el portal de gestión de Stripe. Tu suscripción seguirá activa hasta el final del período actual.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-gradient-to-r from-yellow/10 to-orange/10 rounded-xl p-5 border border-yellow/20">
                        <div className="flex items-center mb-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow to-orange flex items-center justify-center text-white shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="font-medium text-gray-800">3 días de prueba gratis</h3>
                            <p className="text-sm text-gray-600">Sin compromiso, cancela cuando quieras</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-dashed border-yellow/30">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Después del período de prueba</span>
                            <span className="text-orange font-bold">35€/mes</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Se te cobrará automáticamente después del período de prueba a menos que canceles antes
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={redirectToCheckout}
                        disabled={isLoadingStripe}
                        className="w-full bg-gradient-to-r from-orange to-yellow text-white hover:opacity-90 h-12 font-medium"
                      >
                        {isLoadingStripe ? (
                          <span className="flex items-center gap-2">
                            <Spinner size="sm" /> Redirigiendo a Stripe...
                          </span>
                        ) : (
                          'Comenzar Prueba Gratuita'
                        )}
                      </Button>
                      
                      <p className="text-center text-xs text-gray-500">
                        Al suscribirte, aceptas nuestros <a href="/terminos-condiciones" className="text-orange hover:underline">Términos y Condiciones</a> y <a href="/politica-privacidad" className="text-orange hover:underline">Política de Privacidad</a>
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
              
              {!isSubscriptionActiveOrTrial && (
                <div className="mt-4 flex items-center justify-center gap-1 text-sm text-gray-500">
                  <ShieldCheck className="h-4 w-4 text-mint" />
                  <span>Procesamiento seguro con Stripe</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Update FAQ section with new pricing */}
          {!isSubscriptionActiveOrTrial && (
            <div className="mt-16 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-8">Preguntas frecuentes</h2>
              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {[
                  {
                    q: "¿Puedo cancelar en cualquier momento?",
                    a: "Sí, puedes cancelar tu suscripción en cualquier momento desde el portal de gestión de Stripe. Si cancelas durante el período de prueba, no se te cobrará nada."
                  },
                  {
                    q: "¿Qué métodos de pago aceptan?",
                    a: "Aceptamos todas las tarjetas de crédito y débito principales a través de nuestra plataforma segura de Stripe."
                  },
                  {
                    q: "¿Qué pasa cuando termina mi prueba gratuita?",
                    a: "Si decides no cancelar, se te cobrará automáticamente 35€ al mes por tu suscripción Premium."
                  },
                  {
                    q: "¿Puedo cambiar de plan más adelante?",
                    a: "Actualmente solo ofrecemos un plan Premium con todas las funciones incluidas por 35€ al mes."
                  }
                ].map((faq, i) => (
                  <div key={i} className="text-left p-5 rounded-xl bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all border border-gray-100">
                    <h3 className="font-medium text-gray-800 mb-2">{faq.q}</h3>
                    <p className="text-gray-600 text-sm">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Subscription;
